/**
 * 교육실적 ag-grid 인라인 편집 지원
 * - applyProgramEditing: programColDefs 트리에 editable/cellEditor/valueSetter(검증) 주입
 * - mapProgramRowToEditDTO: 그리드 행 → PATCH /program/{id} 요청 바디 매핑
 *
 * 편집 활성화는 grid context.editMode 로 제어(토글 시 refreshCells).
 */
import { message } from 'antd'
import { PROGRAM_FIELD_SPEC, validateProgramField } from 'utils/validation/programEditSpec'

/**
 * 인라인 편집 제외 컬럼
 * - programId/regionId/areaName : 식별자·파생 표시값
 * - status                      : 운영상태는 인라인 편집 대상에서 제외(정책)
 * - isCompleted                 : 아카이브는 전용 API(PATCH /program/{id}/archive) 사용
 * - mainTutorName/subTutorName  : 강사 매칭은 상세 화면의 강사 목록에서 관리
 * - isShortTerm                 : 교육시간으로 파생되는 값
 * - createUser/updateUser/createdAt/updatedAt/detail/*Count : 표시 전용
 */
export const PROGRAM_EDIT_EXCLUDED = new Set([
    'programId',
    'regionId',
    'areaName',
    'status',
    'isCompleted',
    'isShortTerm',
    'mainTutorName',
    'subTutorName',
    'mainTutorCount',
    'subTutorCount',
    'detail',
    'createUser',
    'updateUser',
    'createdAt',
    'updatedAt',
])

const editableByMode = params => !!params.context?.editMode

/**
 * 도서벽지 기관은 기관명·주소를 인라인에서 잠근다.
 * 상세 화면에는 "기관명 입력 → 도서벽지 목록 조회 → 주소 자동입력(또는 주소 선택)" 흐름이 있는데,
 * 셀 단위 편집으로는 그 조회/연동을 재현할 수 없어 잘못된 기관명·주소가 저장될 수 있다.
 * → 도서벽지 행은 상세 화면에서 수정하도록 유도한다.
 */
const ISLAND_LOCKED_FIELDS = new Set(['organizationName', 'address'])
const editableUnlessIsland = params =>
    !!params.context?.editMode && params.data?.organizationType !== '도서벽지'

/**
 * 소외여부: 도서벽지/특수학교/지역아동센터는 정의상 항상 소외.
 * 상세 화면(DemandAgencyInfoForm)과 동일하게 구분을 바꾸면 자동으로 맞추고, 직접 수정은 막는다.
 */
const ALWAYS_UNDERSERVED = ['도서벽지', '특수학교', '지역아동센터']
const editableUnlessForcedUnderserved = params =>
    !!params.context?.editMode && !ALWAYS_UNDERSERVED.includes(params.data?.organizationType)

// 공통 valueSetter: 검증 통과 시에만 반영 (실패 시 셀 값 유지 + 안내)
const makeValueSetter = (key, transform) => params => {
    const raw = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue
    const next = transform ? transform(raw) : raw
    const msg = validateProgramField(key, next, { ...params.data, [key]: next })
    if (msg) {
        message.error(msg)
        return false
    }
    params.data[key] = next
    return true
}

/**
 * 구분(organizationType) 전용 valueSetter.
 * 값 변경이 같은 행의 다른 셀에 영향을 주므로 두 가지를 함께 처리한다.
 *  1) 소외 3종이면 소외여부를 자동으로 true, 그 외로 바꾸면 false (상세 화면과 동일한 규칙)
 *  2) 행 전체 refresh — 기관명·주소·소외여부의 잠금 표시(회색)는 cellClass 로 그려지는데
 *     셀 하나만 바뀌면 형제 셀은 다시 그려지지 않아 색이 그대로 남는다.
 */
const organizationTypeValueSetter = params => {
    const next = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue
    const msg = validateProgramField('organizationType', next, { ...params.data, organizationType: next })
    if (msg) {
        message.error(msg)
        return false
    }
    params.data.organizationType = next
    params.data.isUnderserved = ALWAYS_UNDERSERVED.includes(next)

    // valueSetter 반환 전에 부르면 갱신 중인 상태와 겹치므로 다음 틱에 실행
    setTimeout(() => params.api?.refreshCells({ rowNodes: [params.node], force: true }), 0)
    return true
}

// 숫자 컬럼: 빈 값은 null, 그 외 Number 로 변환해서 저장
const toNumberOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))

/**
 * 컬럼 헤더 툴팁 — 편집 규칙과 주의사항을 마우스오버로 안내.
 * 유효성 메시지는 어겼을 때만 보이므로, 미리 알 수 있게 헤더에 붙인다.
 */
const HEADER_NOTES = {
    // 편집 불가 사유
    programId: '식별자이므로 수정할 수 없습니다.',
    regionId: '지역은 수정할 수 없습니다.',
    status: '운영상태는 상세 화면에서 변경합니다.',
    isCompleted: '아카이브는 상세 화면의 [아카이브] 버튼으로 처리합니다.',
    mainTutorName: '강사 매칭은 상세 화면에서 관리합니다.',
    subTutorName: '강사 매칭은 상세 화면에서 관리합니다.',
    isShortTerm: '교육시간에 따라 자동으로 정해집니다.',

    // 편집 규칙
    educationType: '센터교육·찾아가는 교육·기타 중에서 선택합니다.',
    course: '정규과정·비정규과정 중에서 선택합니다.',
    grade: '초등·중등·고등·혼합 중에서 선택합니다.',
    classroom: '선택 항목입니다. 형식 제한 없이 입력합니다. (예: 6-1)',
    registrationYear:
        '2019~2026 중에서 선택합니다. 변경 후 저장하면 현재 연도 필터에서 해당 행이 보이지 않을 수 있습니다.',
    programName: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다.',
    category1: '필수 항목입니다. 목록에서 선택합니다(해당없음은 선택할 수 없습니다).',
    category2: '선택 항목입니다. 해당없음을 포함한 목록에서 선택합니다.',
    category3: '선택 항목입니다. 해당없음을 포함한 목록에서 선택합니다.',
    classHours: '0 이상의 숫자를 입력합니다.',
    numberOfSession: '선택 항목입니다. 입력할 경우 0 이상의 숫자여야 합니다.',
    numberOfApplicants: '0 이상의 숫자를 입력합니다. 수료인원보다 적을 수 없습니다.',
    numberOfGraduates: '0 이상의 숫자를 입력합니다. 수강인원보다 많을 수 없습니다.',
    startDate: 'YYYY-MM-DD 형식으로 실제 존재하는 날짜를 입력합니다.',
    endDate: 'YYYY-MM-DD 형식으로 입력합니다. 시작일보다 빠를 수 없습니다.',
    isUnderserved: '도서벽지·특수학교·지역아동센터는 자동으로 소외 처리되며 수정할 수 없습니다.',
    organizationType: '변경하면 소외여부가 자동으로 조정됩니다.',
    organizationName: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다. 도서벽지는 상세 화면에서만 수정합니다.',
    address: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다. 도서벽지는 상세 화면에서만 수정합니다.',
    managerName: '선택 항목입니다. 입력할 경우 2자 이상이어야 하며 숫자만으로는 입력할 수 없습니다.',
    managerPosition: '선택 항목입니다. 복지사·센터장·시설장·기타 중에서 선택합니다.',
    managerTelNumber: '선택 항목입니다. 숫자와 - 를 포함해 11~13자로 입력합니다. (예: 043-1234-1235)',
    managerPhoneNumber: '선택 항목입니다. 010-0000-0000 형식으로 입력합니다.',
    managerEmail: '선택 항목입니다. @ 와 . 을 포함해야 합니다.',
    managerRemark: '선택 항목입니다. 형식 제한 없이 입력합니다.',
}

/** 헤더 툴팁 주입 (편집 여부와 무관하게 모든 컬럼에 적용) */
const withHeaderNote = col => {
    const note = HEADER_NOTES[col.field]
    return note ? { ...col, headerTooltip: note } : col
}

// 필터·그룹 전용 컬럼(활동연도와 같은 field 를 공유). 편집은 '활동연도' 컬럼에서만 한다.
const EDIT_EXCLUDED_COL_IDS = new Set(['registrationYearFilter'])

/** 단일 컬럼에 편집 속성 주입 (해당되면 새 객체 반환, 아니면 원본) */
const decorateColumn = col => {
    if (!col || !col.field || PROGRAM_EDIT_EXCLUDED.has(col.field)) return col
    if (EDIT_EXCLUDED_COL_IDS.has(col.colId)) return col

    const { field } = col
    const spec = PROGRAM_FIELD_SPEC[field]
    if (!spec) return col // 스펙에 없는 컬럼은 읽기 전용 유지

    // 행 값에 따라 잠기는 필드는 params 를 보고 판단하는 editable 함수를 쓴다
    let editable = editableByMode
    if (ISLAND_LOCKED_FIELDS.has(field)) editable = editableUnlessIsland
    if (field === 'isUnderserved') editable = editableUnlessForcedUnderserved

    if (spec.editor === 'boolean') {
        // ag-grid 기본 boolean 에디터(체크박스)
        return { ...col, editable, cellDataType: 'boolean' }
    }

    if (spec.editor === 'select') {
        return {
            ...col,
            editable,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: spec.options },
            valueSetter: field === 'organizationType' ? organizationTypeValueSetter : makeValueSetter(field),
        }
    }

    if (spec.editor === 'number') {
        return {
            ...col,
            editable,
            cellEditor: 'agNumberCellEditor',
            valueSetter: makeValueSetter(field, toNumberOrNull),
        }
    }

    // text 계열
    return {
        ...col,
        editable,
        cellEditor: 'agTextCellEditor',
        valueSetter: makeValueSetter(field),
    }
}

/** programColDefs(그룹 포함) 전체에 편집 속성 + 헤더 툴팁 주입 */
export const applyProgramEditing = colDefs =>
    colDefs.map(col => {
        if (col.children) {
            return { ...col, children: col.children.map(c => withHeaderNote(decorateColumn(c))) }
        }
        return withHeaderNote(decorateColumn(col))
    })

/**
 * 그리드 행 → PATCH /program/{id} 바디.
 *
 * 주의: 이 API 는 부분 수정이 아니라 전체 갱신이며, 컨트롤러가 필수값 15개
 * (category1/programName/educationType/course/classHours/numberOfApplicants/
 *  numberOfGraduates/status/startDate/endDate/registrationYear/
 *  organizationType/organizationName/grade/address)를 모두 요구한다.
 * → 편집하지 않은 필드도 그리드 행 값 그대로 함께 보내야 한다.
 *
 * 표시 전용 필드만 제거하고 나머지는 유지한다.
 * files 는 넘기지 않는다(컨트롤러가 files 없으면 첨부를 건드리지 않음 — 기존 첨부 보존).
 */
export const mapProgramRowToEditDTO = row => {
    const {
        areaName,
        isShortTerm,
        mainTutorName,
        subTutorName,
        mainTutorCount,
        subTutorCount,
        createUser,
        updateUser,
        createdAt,
        updatedAt,
        ...rest
    } = row

    return rest
}
